import { Injectable, inject } from "@angular/core";
import { AppAction, AuthScopes, isActionAllowed, isReadOnlyContextType, OrganizationTypeEnumTypes, UserContextType } from "tht-impianti-cl";
import { TokenStorageService } from "./token-storage.service";
import { BaseDataService } from "src/ancestors/base-data.service";

/**
 * L'unico punto del frontend che decide se un'azione è permessa.
 *
 * La regola NON è scritta qui: la applica `isActionAllowed` di `tht-impianti-cl`, la stessa
 * identica funzione che il backend usa dentro `requireAction`. È la ragione per cui vive in CL:
 * se il frontend avesse una sua copia della logica, prima o poi mostrerebbe un pulsante che il
 * backend risponde 403, o — peggio — nasconderebbe un'azione che all'utente spetta.
 *
 * Le due dimensioni arrivano entrambe dal token salvato:
 * - le AUTH da `jwtPayload.auths`, popolate dal backend leggendo `t_profile_auth`;
 * - il contesto da `userContextType`, che il backend allega alle informazioni di login.
 *
 * Entrambe sono sincrone, e non per caso: le guardie di rotta e la direttiva `*appCan` devono
 * decidere durante il rendering, senza poter aspettare una risposta HTTP.
 *
 * COSA NON FA. Non sostituisce il controllo del backend: nasconde, non protegge. Un utente che
 * chiama l'API a mano riceve comunque 403. Serve a non mostrare comandi che non porterebbero da
 * nessuna parte.
 */
@Injectable({ providedIn: "root" })
export class PermissionService extends BaseDataService {

    private tkStorage = inject(TokenStorageService);

    /** L'utente può eseguire l'azione, tenuto conto del suo ruolo E del suo contesto */
    public can(action: AppAction): boolean {
        if (this.isSuperAdmin()) {
            // un superadmin ha modo di eseguire ogni operazione
            return true;
        }
        return isActionAllowed({
            auths: this.getAuths(),
            contextType: this.contextType(),
            action
        });
    }

    /** Almeno una delle azioni è permessa. Utile per decidere se mostrare un gruppo di comandi */
    public canAny(...actions: AppAction[]): boolean {
        return actions.some(action => this.can(action));
    }

    private isSuperAdmin() {
        return this.tkStorage.retriveToken(this.tokenKey)?.jwtPayload?.profiles?.includes("SUPER_ADMIN") ?? false;
    }

    /** Tutte le azioni sono permesse */
    public canAll(...actions: AppAction[]): boolean {
        return actions.length > 0 && actions.every(action => this.can(action));
    }

    /**
     * Contesto dell'utente: il tipo dell'organizzazione a cui appartiene.
     * `null` se non ne ha una, o se il token non porta l'informazione (sessioni aperte prima che il
     * backend iniziasse a inviarla).
     */
    public contextType(): UserContextType | null {
        return this.tkStorage.retriveToken(this.tokenKey)?.userContextType ?? null;
    }

    /**
     * L'utente opera in sola lettura (organizzazione di tipo Supervisore).
     *
     * Da usare per gli avvisi e per disabilitare interi form in un colpo solo, non al posto di
     * `can()`: la sola lettura è già dentro la regola.
     */
    public isReadOnly(): boolean {
        return isReadOnlyContextType(this.contextType());
    }

    /**
     * L'utente puo' vedere e usare le MANUTENZIONI INTERNE.
     *
     * L'analisi Milestone 3 le dichiara «accessibili solo da contesti di tipo Cliente o
     * Supervisore», e il divieto per il FORNITORE e' assoluto: «nessun utente di contesto
     * Fornitore, a prescindere dal ruolo».
     *
     * Perche' non basta `can(AppActions.READ_INTERNAL_MAINTENANCE_LIST)`: `isActionAllowed` valuta
     * `BYPASS_ALL_POLICIES` PRIMA dei contesti, e `can()` qui sopra ha in piu' la scorciatoia sul
     * profilo SUPER_ADMIN — quindi un SuperAdmin di organizzazione fornitrice passerebbe la policy,
     * vedrebbe la voce di menu e poi prenderebbe 401 dal backend, che il divieto lo applica sul
     * dato (`AuthorizationUtils.canAccessInternalMaintenances`). Questo metodo e' il gemello
     * frontend di quella funzione, e le due vanno tenute allineate.
     *
     * Il contesto ASSENTE passa solo con `BYPASS_ALL_POLICIES`: e' l'amministratore di tenant che
     * non appartiene ad alcuna organizzazione, e senza la deroga non vedrebbe la sezione.
     */
    public canAccessInternalMaintenances(): boolean {
        const contextType = this.contextType();
        if (contextType === OrganizationTypeEnumTypes.SUPPLIER) {
            return false;
        }
        if (contextType === OrganizationTypeEnumTypes.CLIENT || contextType === OrganizationTypeEnumTypes.SUPERVISOR) {
            return true;
        }
        return this.getAuths().includes(AuthScopes.BYPASS_ALL_POLICIES);
    }

    /**
     * L'utente puo' cambiare il TIPO di un'organizzazione.
     *
     * Il tipo E' il contesto dei suoi utenti: portarlo da Fornitore a Cliente promuoverebbe in un
     * colpo solo sé stessi e i propri colleghi, e da Cliente si creano organizzazioni, utenti e
     * aree — tutte azioni `CLIENT_ONLY`. E' quindi il modo di uscire da quei vincoli, e va chiuso.
     *
     * Non e' un'azione ma una regola di CAMPO, e per questo non passa da `can()`: `ACTION_POLICIES`
     * governa le azioni intere, mentre qui il permesso dipende dal valore inviato. Il gemello
     * backend e' la guardia in `OrganizationService.updateOrganization`, e le due vanno tenute
     * allineate — questa nasconde, quella nega.
     *
     * La deroga e' `BYPASS_ALL_POLICIES` e non il nome del profilo, come sul backend: il SuperAdmin
     * amministra il tenant e tipicamente non appartiene ad alcuna organizzazione, quindi il solo
     * confronto sul contesto lo escluderebbe proprio dove serve — correggere un tipo sbagliato in
     * creazione.
     */
    public canChangeOrganizationType(): boolean {
        if (this.contextType() === OrganizationTypeEnumTypes.CLIENT) {
            return true;
        }
        return this.getAuths().includes(AuthScopes.BYPASS_ALL_POLICIES);
    }

    /**
     * L'utente puo' SCRIVERE su un dato che appartiene all'organizzazione indicata.
     *
     * E' il perimetro del rapportino: il consuntivo e' il lavoro dell'organizzazione che lo esegue
     * — quella del ticket, o del contratto se il ticket non ne ha — e lo compila solo lei. Non
     * basta il CONTESTO, perche' in contesto Cliente il perimetro di lettura e' l'intero tenant: un
     * Amministrativo del Cliente vedeva — e quindi modificava — il rapportino di un Fornitore.
     *
     * Non passa da `can()` perche' non e' un'azione ma un PERIMETRO: `ACTION_POLICIES` ammette o
     * nega un contesto intero, mentre qui la risposta e' "si', ma solo sulle cose della mia
     * organizzazione". Il gemello backend e' `AuthorizationUtils.requireOwnOrganizationMatch`, e le
     * due vanno tenute allineate — questa nasconde, quella nega.
     *
     * Dato SENZA organizzazione: nessun perimetro da confrontare, si passa. La deroga e'
     * `BYPASS_ALL_POLICIES` e non il nome del profilo, come sul backend: il SuperAdmin amministra
     * il tenant e tipicamente non appartiene ad alcuna organizzazione.
     */
    public canWriteForOrganization(organizationId?: string | null): boolean {
        if (!organizationId) {
            return true;
        }
        if (this.getAuths().includes(AuthScopes.BYPASS_ALL_POLICIES)) {
            return true;
        }
        return organizationId === this.organizationId();
    }

    /**
     * Sul dato di QUESTA organizzazione l'utente vede solo cio' che gli e' stato CONDIVISO.
     *
     * E' il perimetro delle compilazioni di checklist, e vale ticket per ticket: chi opera in
     * contesto Cliente (o Supervisore) vede tutte le compilazioni dei ticket della PROPRIA
     * organizzazione — e' il suo lavoro — e le sole compilazioni condivise sui ticket di un'altra,
     * tipicamente un Fornitore. La condivisione e' il gesto con cui quel lavoro gli viene
     * consegnato, e prima di quello non c'e' nulla da mostrargli.
     *
     * Si appoggia a `canWriteForOrganization`, che e' gia' la domanda "questo dato e' della mia
     * organizzazione?", cosi' le due regole non possono divergere: dove si puo' scrivere si vede
     * tutto, dove non si puo' si vedono le sole condivise. Il gemello backend e'
     * `ChecklistSubmissionService.seesOnlyPublishedSubmissions` piu' il predicato per riga del
     * repository: questo nasconde, quello nega.
     *
     * Il contesto FORNITORE non e' confinato: il suo perimetro e' gia' per impianto e per area.
     */
    public seesOnlySharedChecklistsFor(organizationId?: string | null): boolean {
        const contextType = this.contextType();
        const isConfinedContext = contextType === OrganizationTypeEnumTypes.CLIENT
            || contextType === OrganizationTypeEnumTypes.SUPERVISOR;
        return isConfinedContext && !this.canWriteForOrganization(organizationId);
    }

    /** ID dell'organizzazione di appartenenza, da cui deriva il contesto */
    public organizationId(): string | null {
        return this.tkStorage.retriveToken(this.tokenKey)?.organizationId ?? null;
    }

    public getAuths(): string[] {
        return this.tkStorage.retriveToken(this.tokenKey)?.jwtPayload?.auths ?? [];
    }
}

/**
 * Filtra una lista tenendo solo le voci la cui azione è permessa.
 *
 * Serve dove la direttiva strutturale non arriva: le voci di una tendina (gli stati selezionabili
 * di un ticket, le operazioni di un menu contestuale) sono dati, non elementi del template.
 *
 * @param items - Voci da filtrare
 * @param actionOf - Azione richiesta dalla voce; `null`/`undefined` = voce sempre visibile
 * @param permissions - Il servizio, che il chiamante ha già iniettato
 */
export function filterByPermission<T>(
    items: T[],
    actionOf: (item: T) => AppAction | null | undefined,
    permissions: PermissionService
): T[] {
    return (items ?? []).filter(item => {
        const action = actionOf(item);
        return !action || permissions.can(action);
    });
}
